import { useTranslation } from 'react-i18next';
import { BrandingColors, BrandingAssets, BrandingFont } from '@/types/branding.types';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';

/**
 * BrandingPreview Component
 *
 * Showcases all 19 customizable colors and both headline + body fonts
 * in context. Uses inline styles exclusively for color/font values so
 * the preview reflects draft state without touching page-level CSS variables.
 */

interface BrandingPreviewProps {
  branding: {
    colors?: BrandingColors;
    assets?: BrandingAssets;
    font?: BrandingFont;
    headlineFont?: BrandingFont;
  };
}

// Fallback defaults matching DEFAULT_COLORS in whitelabel-defaults.ts
const D = {
  primary: '#69C7C1',
  secondary: '#074F47',
  secondaryRgba: 'rgba(7, 79, 71, 0.4)',
  gray: '#6c757d',
  gray2: '#DDE2E5',
  gray400: '#ced4da',
  gray600: '#6c757d',
  grayStone: '#54595f',
  light: '#f0f2f5',
  calendarHighlight: '#F6F1EE',
  blackButton: '#000000',
  coral: '#ff5757',
  softCoral: '#FBD3D1',
  softMint: '#D5EEEB',
  peach: '#FBCCAE',
  lightTealGray: '#B9C7C7',
  sageGray: '#718080',
  warmGray: '#DAD2D1',
  offWhite: '#F6F8F5',
};

// Tiny helper: return draft value or fallback
function c(draft: string | undefined, fallback: string): string {
  return draft || fallback;
}

// Section heading shared style
const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
    {children}
  </h3>
);

// Single color swatch
interface SwatchProps {
  color: string;
  label: string;
}
const Swatch = ({ color, label }: SwatchProps) => (
  <div className="flex flex-col items-center gap-1.5">
    <div
      className="w-full aspect-square rounded-lg border border-black/10 shadow-sm"
      style={{ backgroundColor: color }}
      title={color}
    />
    <div className="text-center w-full">
      <p className="text-[10px] font-medium text-gray-700 leading-tight truncate">
        {label}
      </p>
      <p className="text-[9px] text-gray-400 leading-tight font-mono">
        {color}
      </p>
    </div>
  </div>
);

export const BrandingPreview = ({ branding }: BrandingPreviewProps) => {
  const { t } = useTranslation('settings');
  const { colors, assets, font, headlineFont } = branding;

  // Resolved colors — draft values with fallbacks
  const primary = c(colors?.primary, D.primary);
  const secondary = c(colors?.secondary, D.secondary);
  const coral = c(colors?.coral, D.coral);
  const softCoral = c(colors?.softCoral, D.softCoral);
  const softMint = c(colors?.softMint, D.softMint);
  const peach = c(colors?.peach, D.peach);
  const offWhite = c(colors?.offWhite, D.offWhite);
  const gray = c(colors?.gray, D.gray);
  const gray2 = c(colors?.gray2, D.gray2);
  const gray400 = c(colors?.gray400, D.gray400);
  const gray600 = c(colors?.gray600, D.gray600);
  const grayStone = c(colors?.grayStone, D.grayStone);
  const lightTealGray = c(colors?.lightTealGray, D.lightTealGray);
  const sageGray = c(colors?.sageGray, D.sageGray);
  const warmGray = c(colors?.warmGray, D.warmGray);
  const light = c(colors?.light, D.light);
  const calendarHighlight = c(colors?.calendarHighlight, D.calendarHighlight);
  const blackButton = c(colors?.blackButton, D.blackButton);

  // Resolved fonts
  const bodyFamily = font?.family || 'DM Sans, sans-serif';
  const headlineFamily = headlineFont?.family || 'Wagner, sans-serif';
  const bodyLabel = font?.family?.replace(/['"]/g, '').split(',')[0] || 'DM Sans';
  const headlineLabel = headlineFont?.family?.replace(/['"]/g, '').split(',')[0] || 'Wagner';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
          {t('settings_preview_title', { defaultValue: 'Branding Preview' })}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('settings_preview_description', { defaultValue: 'Live preview of your brand colors and typography.' })}
        </p>
      </div>

      <div className="space-y-8">

        {/* ── 1. COLOR PALETTE ─────────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_colors', { defaultValue: 'Color Palette' })}
          </SectionHeading>

          {/* Brand Colors */}
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-2">
            {t('settings_preview_colors_brand', { defaultValue: 'Brand' })}
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-5">
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={primary} label="Primary" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={secondary} label="Secondary" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={coral} label="Coral" />
          </div>

          {/* Soft / Background tones */}
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-2">
            {t('settings_preview_colors_soft', { defaultValue: 'Soft Backgrounds' })}
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-5">
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={softMint} label="Soft Mint" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={softCoral} label="Soft Coral" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={peach} label="Peach" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={offWhite} label="Off White" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={light} label="Light" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={calendarHighlight} label="Calendar" />
          </div>

          {/* Neutral / Gray scale */}
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-2">
            {t('settings_preview_colors_neutrals', { defaultValue: 'Neutrals' })}
          </p>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 mb-5">
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={gray} label="Gray" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={gray2} label="Gray 2" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={gray400} label="Gray 400" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={gray600} label="Gray 600" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={grayStone} label="Stone" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={lightTealGray} label="Teal Gray" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={sageGray} label="Sage" />
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={warmGray} label="Warm" />
          </div>

          {/* Utility */}
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mb-2">
            {t('settings_preview_colors_utility', { defaultValue: 'Utility' })}
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Swatch color={blackButton} label="Dark Button" />
          </div>
        </section>

        {/* ── 2. GRADIENTS ─────────────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_gradients', { defaultValue: 'Gradients' })}
          </SectionHeading>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div
                className="h-20 rounded-lg border border-black/5 shadow-sm"
                style={{
                  background: `linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, ${softMint} 100%)`,
                }}
              />
              <p className="text-[10px] text-gray-500 mt-1 font-medium">
                {t('settings_preview_gradient_mint', { defaultValue: 'Mint Gradient' })}
              </p>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <p className="text-[9px] text-gray-400">#FFF → {softMint}</p>
            </div>
            <div>
              <div
                className="h-20 rounded-lg border border-black/5 shadow-sm"
                style={{
                  background: `linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, ${softCoral} 100%)`,
                }}
              />
              <p className="text-[10px] text-gray-500 mt-1 font-medium">
                {t('settings_preview_gradient_coral', { defaultValue: 'Coral Gradient' })}
              </p>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <p className="text-[9px] text-gray-400">#FFF → {softCoral}</p>
            </div>
          </div>
        </section>

        {/* ── 3. TYPOGRAPHY ────────────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_typography', { defaultValue: 'Typography' })}
          </SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Headline font */}
            <div
              className="rounded-xl border border-gray-100 bg-gray-50/60 p-5 space-y-2"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {t('settings_preview_font_headline', { defaultValue: 'Headline Font' })} — {headlineLabel}
              </p>
              {/* eslint-disable i18next/no-literal-string */}
              <p className="text-3xl sm:text-4xl font-bold leading-tight" style={{ fontFamily: headlineFamily, color: secondary }}>Aa</p>
              <p className="text-xl sm:text-2xl font-semibold leading-snug" style={{ fontFamily: headlineFamily }}>Heading One</p>
              <p className="text-lg sm:text-xl font-semibold leading-snug text-gray-700" style={{ fontFamily: headlineFamily }}>Heading Two</p>
              {/* eslint-enable i18next/no-literal-string */}
            </div>

            {/* Body font */}
            <div
              className="rounded-xl border border-gray-100 bg-gray-50/60 p-5 space-y-2"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {t('settings_preview_font_body', { defaultValue: 'Body Font' })} — {bodyLabel}
              </p>
              {/* eslint-disable i18next/no-literal-string */}
              <p className="text-3xl sm:text-4xl font-bold leading-tight" style={{ fontFamily: bodyFamily, color: primary }}>Aa</p>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: bodyFamily }}>Body text — The quick brown fox jumps over the lazy dog. Pack my box with five dozen liquor jugs.</p>
              <p className="text-sm leading-relaxed text-gray-500" style={{ fontFamily: bodyFamily }}>Small caption text. Used for metadata, labels, and helper copy.</p>
              {/* eslint-enable i18next/no-literal-string */}
            </div>
          </div>
        </section>

        {/* ── 4. BUTTONS ───────────────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_buttons', { defaultValue: 'Buttons' })}
          </SectionHeading>
          <div className="flex flex-wrap gap-3 items-center">
            {/* Primary */}
            <button
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
              style={{ backgroundColor: primary, fontFamily: bodyFamily }}
            >
              {t('settings_preview_button_primary', { defaultValue: 'Primary Action' })}
            </button>

            {/* Secondary */}
            <button
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
              style={{ backgroundColor: secondary, fontFamily: bodyFamily }}
            >
              {t('settings_preview_button_secondary', { defaultValue: 'Secondary' })}
            </button>

            {/* Coral */}
            <button
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
              style={{ backgroundColor: coral, fontFamily: bodyFamily }}
            >
              {t('settings_preview_button_coral', { defaultValue: 'Coral' })}
            </button>

            {/* Outline */}
            <button
              className="px-4 py-2 rounded-lg text-sm font-semibold border-2 bg-transparent"
              style={{
                borderColor: primary,
                color: primary,
                fontFamily: bodyFamily,
              }}
            >
              {t('settings_preview_button_outline', { defaultValue: 'Outline' })}
            </button>

            {/* Dark */}
            <button
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
              style={{ backgroundColor: blackButton, fontFamily: bodyFamily }}
            >
              {t('settings_preview_button_dark', { defaultValue: 'Dark' })}
            </button>
          </div>

          {/* Badge row */}
          <div className="flex flex-wrap gap-2 mt-3">
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium leading-none text-white"
              style={{ backgroundColor: primary }}
            >
              {t('settings_preview_badge_primary', { defaultValue: 'Primary' })}
            </span>
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium leading-none text-white"
              style={{ backgroundColor: secondary }}
            >
              {t('settings_preview_badge_secondary', { defaultValue: 'Secondary' })}
            </span>
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium leading-none text-white"
              style={{ backgroundColor: coral }}
            >
              {t('settings_preview_badge_coral', { defaultValue: 'Coral' })}
            </span>
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium leading-none"
              style={{ backgroundColor: softMint, color: secondary }}
            >
              {t('settings_preview_badge_mint', { defaultValue: 'Mint' })}
            </span>
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium leading-none"
              style={{ backgroundColor: softCoral, color: coral }}
            >
              {t('settings_preview_badge_soft_coral', { defaultValue: 'Soft Coral' })}
            </span>
          </div>
        </section>

        {/* ── 5. NAVIGATION BAR ────────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_nav', { defaultValue: 'Navigation Bar' })}
          </SectionHeading>
          <div
            className="rounded-xl border p-3 flex items-center gap-6"
            style={{ backgroundColor: light, borderColor: gray2 }}
          >
            {/* Logo / brand mark */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {assets?.logoSmall ? (
                <img src={assets.logoSmall} alt="Logo" className="h-7 w-7 object-contain" />
              ) : (
                <div
                  className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: primary }}
                >
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  A
                </div>
              )}
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <span className="font-bold text-sm" style={{ fontFamily: headlineFamily, color: secondary }}>Brand Name</span>
            </div>

            {/* Nav links */}
            <nav className="flex gap-4 text-sm" style={{ fontFamily: bodyFamily }}>
              <a
                href="#"
                className="font-medium transition-opacity"
                style={{ color: primary }}
                onClick={(e) => e.preventDefault()}
              >
                {t('dashboard', { defaultValue: 'Dashboard' })}
              </a>
              <a
                href="#"
                className="transition-opacity"
                style={{ color: gray }}
                onClick={(e) => e.preventDefault()}
              >
                {t('calendar', { defaultValue: 'Calendar' })}
              </a>
              <a
                href="#"
                className="transition-opacity"
                style={{ color: gray }}
                onClick={(e) => e.preventDefault()}
              >
                {t('settings_title', { defaultValue: 'Settings' })}
              </a>
            </nav>

            {/* Right action */}
            <div className="ml-auto">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: secondary }}
              >
                {/* eslint-disable-next-line i18next/no-literal-string */}
                JD
              </div>
            </div>
          </div>
        </section>

        {/* ── PAGE HEADER MOCKUP ─────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_page_header', { defaultValue: 'Page Header' })}
          </SectionHeading>
          <div
            className="rounded-xl overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)`,
            }}
          >
            <div className="px-4 py-6 sm:px-6 sm:py-8">
              <h1
                className="text-2xl sm:text-3xl font-bold text-white drop-shadow-sm"
                style={{ fontFamily: headlineFamily }}
              >
                {t('calendar', { defaultValue: 'Calendar' })}
              </h1>
              <p
                className="text-sm mt-1 text-white/70"
                style={{ fontFamily: bodyFamily }}
              >
                {t('settings_preview_page_header_subtitle', { defaultValue: 'Plan and schedule your social media content' })}
              </p>
            </div>
          </div>
        </section>

        {/* ── SIDEBAR MOCKUP ─────────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_sidebar', { defaultValue: 'Sidebar Navigation' })}
          </SectionHeading>
          <div
            className="rounded-xl border w-48 p-3 space-y-1"
            style={{ backgroundColor: '#ffffff', borderColor: gray2, fontFamily: bodyFamily }}
          >
            {/* Active item */}
            <div
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-white"
              style={{ backgroundColor: primary }}
            >
              <Icon name={ICONS.DASHBOARD} size="xs" ariaHidden />
              <span>{t('dashboard', { defaultValue: 'Dashboard' })}</span>
            </div>
            {/* Inactive items */}
            {[
              { icon: ICONS.CALENDAR, label: t('calendar', { defaultValue: 'Calendar' }) },
              { icon: ICONS.LIST, label: t('posts', { defaultValue: 'Posts' }) },
              { icon: ICONS.SETTINGS, label: t('settings_title', { defaultValue: 'Settings' }) },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm"
                style={{ color: grayStone }}
              >
                <Icon name={item.icon} size="xs" ariaHidden />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── 6. CARD SAMPLE ───────────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_card', { defaultValue: 'Card' })}
          </SectionHeading>
          <div
            className="rounded-xl border p-5 space-y-4"
            style={{ backgroundColor: '#ffffff', borderColor: gray2 }}
          >
            {/* Card header */}
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                style={{ backgroundColor: primary }}
              >
                <Icon name={ICONS.STAR} size="sm" ariaHidden />
              </div>
              <div>
                <h4
                  className="font-semibold text-gray-900 text-sm"
                  style={{ fontFamily: headlineFamily }}
                >
                  {t('settings_preview_card_title', { defaultValue: 'Card Title' })}
                </h4>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: gray, fontFamily: bodyFamily }}
                >
                  {t('settings_preview_card_subtitle', { defaultValue: 'Supporting subtitle text' })}
                </p>
              </div>
              <span
                className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: softMint, color: secondary }}
              >
                {t('settings_preview_card_badge', { defaultValue: 'Active' })}
              </span>
            </div>

            {/* Card body - like a post card */}
            <p
              className="text-sm leading-relaxed"
              style={{ color: grayStone, fontFamily: bodyFamily }}
            >
              {t('settings_preview_card_content', {
                defaultValue:
                  'Your next social media post will be styled with these brand colors. This preview shows how cards, buttons, and text appear across your Brand.',
              })}
            </p>

            {/* Tags row */}
            <div className="flex flex-wrap gap-1.5">
              <span
                className="px-2 py-0.5 rounded text-[10px] font-medium"
                style={{ backgroundColor: softMint, color: secondary }}
              >
                {t('post_status_scheduled', { defaultValue: 'Scheduled' })}
              </span>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: softCoral, color: coral }}>Instagram</span>
              <span
                className="px-2 py-0.5 rounded text-[10px] font-medium"
                style={{ backgroundColor: peach, color: grayStone }}
              >
                {t('post_status_pending_review', { defaultValue: 'Pending Review' })}
              </span>
            </div>

            {/* Divider */}
            <div className="border-t" style={{ borderColor: gray2 }} />

            {/* Card footer */}
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                style={{ backgroundColor: primary, fontFamily: bodyFamily }}
              >
                {t('settings_preview_card_action', { defaultValue: 'View Details' })}
              </button>
              <button
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ color: gray, fontFamily: bodyFamily }}
              >
                {t('settings_modal_cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>
          </div>
        </section>

        {/* ── 7. INFO / ALERT BOX ──────────────────────────────────────── */}
        <section>
          <SectionHeading>
            {t('settings_preview_section_alerts', { defaultValue: 'Info & Alert Boxes' })}
          </SectionHeading>
          <div className="space-y-3">
            {/* Mint info box */}
            <div
              className="rounded-lg border p-4 flex gap-3"
              style={{ backgroundColor: softMint, borderColor: lightTealGray }}
            >
              <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden />
              <div style={{ fontFamily: bodyFamily }}>
                <p
                  className="text-sm font-semibold"
                  style={{ color: secondary }}
                >
                  {t('settings_preview_info_title', { defaultValue: 'Information' })}
                </p>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: sageGray }}
                >
                  {t('settings_preview_info_content', {
                    defaultValue: 'This is how informational messages appear using your brand soft-mint and teal-gray palette.',
                  })}
                </p>
              </div>
            </div>

            {/* Coral warning box */}
            <div
              className="rounded-lg border p-4 flex gap-3"
              style={{ backgroundColor: softCoral, borderColor: coral }}
            >
              <Icon name={ICONS.WARNING} size="sm" ariaHidden />
              <div style={{ fontFamily: bodyFamily }}>
                <p
                  className="text-sm font-semibold"
                  style={{ color: coral }}
                >
                  {t('settings_preview_warning_title', { defaultValue: 'Warning' })}
                </p>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: grayStone }}
                >
                  {t('settings_preview_warning_content', {
                    defaultValue: 'This is how warnings look using soft-coral and coral border.',
                  })}
                </p>
              </div>
            </div>

            {/* Calendar highlight sample */}
            <div
              className="rounded-lg border p-4"
              style={{ backgroundColor: calendarHighlight, borderColor: warmGray }}
            >
              <p
                className="text-xs font-semibold mb-1"
                style={{ color: grayStone, fontFamily: bodyFamily }}
              >
                {t('settings_preview_calendar_label', { defaultValue: 'Calendar Highlight' })}
              </p>
              <div className="flex gap-1">
                {/* eslint-disable-next-line i18next/no-literal-string */}
                {(['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const).map((day) => (
                  <div
                    key={day}
                    className="flex-1 text-center py-1.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: day === 'Wed' ? primary : '#ffffff',
                      color: day === 'Wed' ? '#ffffff' : grayStone,
                      fontFamily: bodyFamily,
                    }}
                  >
                    {day}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── 8. LOGO PREVIEW ──────────────────────────────────────────── */}
        {assets?.logoBig && (
          <section>
            <SectionHeading>
              {t('settings_preview_section_logo', { defaultValue: 'Logo' })}
            </SectionHeading>
            <div
              className="rounded-xl border p-6 flex items-center justify-center"
              style={{ backgroundColor: light, borderColor: gray2 }}
            >
              <img
                src={assets.logoBig}
                alt="Brand Logo"
                className="max-h-16 object-contain"
              />
            </div>
          </section>
        )}

      </div>
    </div>
  );
};
